import { useEffect, useMemo, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useNavigate } from 'react-router';
import { isSufeExamClient } from '@/utils/examClient';
import { getExamInfo } from '@/api/start';
import store from '@/stores';
import { examSlice } from '@/stores/slice/exam';
import { finishExamSubmission, getExamSubmissionState } from './examSubmit';
import type { MockExamCourse } from './examSubmit';

interface Options {
  recordId: number;
  course: MockExamCourse;
  ready: boolean;
  expired?: boolean;
  // Pending-submission recovery uses the already frozen server draft.
  save?: (signal: AbortSignal) => Promise<unknown>;
  submit: (signal: AbortSignal) => Promise<unknown>;
}

export function useExamSubmission({recordId, course, ready, expired = false, save, submit}: Options) {
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);
  const scope = useMemo(() => ({
    controller: new AbortController(),
    task: null as Promise<boolean> | null,
    finalizing: false,
    completed: false,
  }), [recordId, course]);

  useEffect(() => {
    const controller = new AbortController();
    scope.controller = controller;
    setShowLoading(false);
    return () => controller.abort();
  }, [scope]);

  const isFinalizing = useMemoizedFn(() => scope.finalizing || (expired && isSufeExamClient()));
  const submitHandle = useMemoizedFn((callback: () => void) => {
    if (!ready || scope.controller.signal.aborted) return Promise.resolve(false);
    if (scope.task) return scope.task;
    if (scope.completed) return Promise.resolve(true);

    const signal = scope.controller.signal;
    scope.finalizing = true;
    setShowLoading(true);
    const task = finishExamSubmission({
      signal,
      readState: async requestSignal => {
        const response = await getExamInfo({record_id: recordId}, {signal: requestSignal});
        if (response.code !== 200) throw new Error('unable to read exam state');
        return getExamSubmissionState(response.data, recordId, course);
      },
      save,
      submit,
    }).then(succeeded => {
      if (signal.aborted) return false;
      if (succeeded) {
        scope.completed = true;
        if (course === 'writing') {
          store.dispatch(examSlice.actions.writingSubmissionConfirmed(recordId));
        }
        callback();
        navigate(`/mockstart/${recordId}`, {replace: true});
      }
      return succeeded;
    }).finally(() => {
      scope.task = null;
      if (!signal.aborted) {
        setShowLoading(false);
        if (!scope.completed && !isSufeExamClient()) scope.finalizing = false;
      }
    });
    scope.task = task;
    return task;
  });

  return {showLoading, submitHandle, isFinalizing};
}
